import React from 'react'
import PropTypes from 'prop-types'
import Spinner from '@boltui/spinner'

import * as Styled from './Button.styles'

const Button = ({
  'data-test': dataTest,
  children,
  className,
  disabled,
  full,
  hasLoading,
  iconOnly,
  iconOnlyText,
  isLoading,
  kind,
  onClick,
  role,
  small,
  tabIndex,
  type,
  ...props
}) => (
  <Styled.Button
    className={className}
    data-test={dataTest}
    disabled={disabled}
    full={full}
    hasLoading={hasLoading}
    iconOnly={iconOnly}
    iconOnlyText={iconOnlyText}
    isLoading={isLoading}
    kind={kind}
    onClick={onClick}
    role={role}
    small={small}
    tabIndex={tabIndex}
    type={type}
    {...props}>
    {iconOnly && <span aria-label={iconOnlyText} />}
    {!hasLoading && !isLoading && children}
    {(hasLoading || isLoading) && (
      <Styled.LoadingContainer aria-busy={isLoading}>
        <Styled.ContentContainer>{children}</Styled.ContentContainer>
        <Styled.SpinnerContainer>
          <Spinner kind={kind === 'primary' ? 'white' : 'green'} size={iconOnly ? 30 : 40} centered />
        </Styled.SpinnerContainer>
      </Styled.LoadingContainer>
    )}
  </Styled.Button>
)

Button.propTypes = {
  /**
   * Specify the content of your Button
   */
  children: PropTypes.node,

  /**
   * Specify an optional className to be added to your Button
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Button for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional prop to specifiy whether the Button should be disabled, or not
   */
  disabled: PropTypes.bool,

  /**
   * Optional prop to specify the type of the Button
   */
  kind: PropTypes.oneOf(['primary', 'secondary']),

  /**
   * Specify whether the Button should be a full variant
   */
  full: PropTypes.bool,

  /**
   * Specify whether the Button should be a small variant
   */
  small: PropTypes.bool,

  /**
   * Specify whether the Button content is only an icon
   */
  iconOnly: PropTypes.bool,

  /**
   * Specify whether the Button has the ability to show a loading spinner. This is required when react is not used client side and you want to use the .isLoading class to trigger the loading state.
   */
  hasLoading: PropTypes.bool,

  /**
   * Specify whether the Button should show a loading spinner
   */
  isLoading: PropTypes.bool,

  /**
   * Text to be shown via `aria-label` when there is no text and only an icon
   */
  iconOnlyText: PropTypes.string,

  /**
   * Optional prop to specify the tabIndex of the Button
   */
  tabIndex: PropTypes.number,

  /**
   * Optional prop to specify the type of the Button
   */
  type: PropTypes.oneOf(['button', 'reset', 'submit']),

  /**
   * Optional prop to specify the role of the Button
   */
  role: PropTypes.string,

  /**
   * Optional prop to pass through onClick events when button is clicked
   */
  onClick: PropTypes.func
}

Button.defaultProps = {
  disabled: false,
  kind: 'primary',
  full: false,
  small: false,
  iconOnly: false,
  isLoading: false,
  hasLoading: false,
  tabIndex: 0,
  type: 'button',
  role: 'button',
  onClick: /* istanbul ignore next */ () => {},
  'data-test': 'component-button'
}

export default Button
